import { CalendarDays } from 'lucide-react';
import { useId, useState, type ReactNode } from 'react';
import { PERIODS, type Period } from './search';
import { PERIOD_LABEL, formatDateChip, periodRange, type DataBounds } from './dates';
import { Chip } from '@/components/ui/chip';
import { inputClass } from '@/components/ui/input';
import { Popover, PopoverContent, PopoverHint, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/cn';
import { formatUtcRange } from '@/lib/format';

type DateRangePickerProps = {
  from: string;
  to: string;
  /** The shortcut the dates stand for, or null for a custom range. */
  period: Period | null;
  /** Data bounds of the scope; null while unknown. */
  bounds: DataBounds | null;
  /** Why this window (evidence at the conclusion), e.g. "… meets the evidence floors for this app." */
  hint?: ReactNode;
  onChange: (from: string, to: string, period: Period | undefined) => void;
};

const DAY = /^\d{4}-\d{2}-\d{2}$/;

/** The date chip ("Sep 16 – Sep 29 · Last 14 days · UTC") and its shortcuts + custom range. */
export function DateRangePicker({ from, to, period, bounds, hint, onChange }: DateRangePickerProps) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState({ from, to });
  const id = useId();
  const known = bounds ?? { first: null, last: null };

  const choose = (next: Period) => {
    const range = periodRange(next, known);
    if (!range) return;
    setOpen(false);
    onChange(range.from, range.to, next);
  };
  const edit = (patch: Partial<typeof draft>) => {
    const next = { ...draft, ...patch };
    setDraft(next);
    if (DAY.test(next.from) && DAY.test(next.to) && next.from <= next.to && (next.from !== from || next.to !== to)) {
      onChange(next.from, next.to, undefined);
    }
  };

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (next) setDraft({ from, to });
      }}
    >
      <PopoverTrigger asChild>
        <Chip icon={<CalendarDays />} label={<span className="sr-only">Dates:</span>} value={formatDateChip(from, to)}>
          <span className="text-muted">
            {' '}
            {period ? PERIOD_LABEL[period] : 'Custom'} · UTC
          </span>
        </Chip>
      </PopoverTrigger>
      <PopoverContent aria-label="Date range" className="w-[min(340px,calc(100vw-32px))]">
        <PopoverHint>Shortcuts</PopoverHint>
        <div className="grid grid-cols-2 gap-1 p-1">
          {PERIODS.map((p) => {
            const unavailable = p === 'lifetime' && !(known.first && known.last);
            return (
              <button
                key={p}
                type="button"
                aria-pressed={period === p}
                disabled={unavailable}
                onClick={() => { choose(p); }}
                className={cn(
                  'rounded-ctl border-0 bg-transparent px-2 py-2 text-left text-sm hover:bg-wash disabled:cursor-not-allowed disabled:opacity-45 aria-pressed:bg-wash aria-pressed:font-medium',
                )}
              >
                {PERIOD_LABEL[p]}
              </button>
            );
          })}
        </div>
        <PopoverHint>Custom range (UTC)</PopoverHint>
        <div className="flex items-center gap-2 px-1.5 py-2">
          <label htmlFor={`${id}-from`} className="sr-only">From date</label>
          <input
            id={`${id}-from`}
            type="date"
            value={draft.from}
            min={known.first ?? undefined}
            max={known.last ?? undefined}
            onChange={(e) => { edit({ from: e.target.value }); }}
            className={cn(inputClass, 'num h-8 min-w-0 flex-1 text-sm')}
          />
          <span aria-hidden="true" className="text-muted">–</span>
          <label htmlFor={`${id}-to`} className="sr-only">To date</label>
          <input
            id={`${id}-to`}
            type="date"
            value={draft.to}
            min={known.first ?? undefined}
            max={known.last ?? undefined}
            onChange={(e) => { edit({ to: e.target.value }); }}
            className={cn(inputClass, 'num h-8 min-w-0 flex-1 text-sm')}
          />
        </div>
        {known.first && known.last ? (
          <p className="mx-1.5 mb-1.5 text-xs text-muted">
            Data available {formatUtcRange(known.first, known.last)}.{hint ? <> {hint}</> : null}
          </p>
        ) : hint ? (
          <p className="mx-1.5 mb-1.5 text-xs text-muted">{hint}</p>
        ) : null}
      </PopoverContent>
    </Popover>
  );
}
